import React from "react";

/*
 * Blixtnedslaget i hjulets mitt. Ritas i hjulets koordinater (700 x 700,
 * mitten i 350, 350): en förgrenad blixt från hjulets överkant rakt ner i
 * pumpan, en vit het glöd, gnistor som sprutar ut och en tryckvåg.
 * Skärmblixten och skakningen ligger i CSS (holidays-fx.css).
 */

type Pt = [number, number];

// Fast, handritad bana så att nedslaget alltid ser lika bra ut.
const MAIN: Pt[] = [
  [430, -720],
  [400, -620],
  [436, -560],
  [388, -470],
  [420, -400],
  [370, -300],
  [408, -240],
  [362, -150],
  [398, -90],
  [396, -30],
  [372, 18],
  [392, 46],
  [358, 92],
  [380, 118],
  [340, 170],
  [366, 196],
  [334, 242],
  [360, 268],
  [338, 310],
  [350, 350],
];

const BRANCHES: Pt[][] = [
  [
    [358, 92],
    [318, 118],
    [326, 140],
    [290, 170],
  ],
  [
    [340, 170],
    [384, 204],
    [376, 226],
    [414, 254],
  ],
  [
    [334, 242],
    [300, 262],
    [306, 284],
    [276, 300],
  ],
];

const path = (pts: Pt[]) => `M${pts.map(([x, y]) => `${x} ${y}`).join(" L")}`;

const SPARKS = Array.from({ length: 18 }, (_, i) => {
  const a = (i / 18) * Math.PI * 2 + (i % 2 ? 0.12 : -0.08);
  const dist = 90 + ((i * 53) % 70);
  return { dx: Math.cos(a) * dist, dy: Math.sin(a) * dist, r: 2.5 + (i % 3), delay: (i % 4) * 25 };
});

export const LightningStrike: React.FC = () => {
  const bolt = path(MAIN);
  const branches = BRANCHES.map(path).join(" ");
  return (
    <g className="wheel-strike" pointerEvents="none">
      <defs>
        <filter id="wheel-strike-blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <filter id="wheel-strike-blur-s" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <radialGradient id="wheel-strike-heat" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="35%" stopColor="#fff3c4" stopOpacity="0.95" />
          <stop offset="70%" stopColor="#ffb347" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#ff7a1a" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Själva blixten: bred glöd, smalare sken och en vit kärna */}
      <g className="wheel-strike-bolt" fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* Mörk kant så att blixten syns även mot det ljusa hjulet */}
        <path d={`${bolt} ${branches}`} stroke="#1a0a14" strokeWidth="22" opacity="0.55" filter="url(#wheel-strike-blur-s)" />
        <path d={`${bolt} ${branches}`} stroke="#8fd4ff" strokeWidth="46" opacity="0.55" filter="url(#wheel-strike-blur)" />
        <path d={`${bolt} ${branches}`} stroke="#d9f1ff" strokeWidth="16" opacity="0.9" filter="url(#wheel-strike-blur-s)" />
        <path d={branches} stroke="#ffffff" strokeWidth="4.5" />
        <path d={bolt} stroke="#ffffff" strokeWidth="9" />
      </g>

      {/* Tryckvåg och vit het glöd i pumpan */}
      <circle className="wheel-strike-ring" cx="350" cy="350" r="120" fill="none" stroke="#fff1d0" strokeWidth="10" />
      <circle className="wheel-strike-ring wheel-strike-ring-2" cx="350" cy="350" r="120" fill="none" stroke="#ffb347" strokeWidth="5" />
      <circle className="wheel-strike-heat" cx="350" cy="350" r="150" fill="url(#wheel-strike-heat)" />

      {/* Gnistor */}
      <g className="wheel-strike-sparks">
        {SPARKS.map((s, i) => (
          <circle
            key={i}
            className="wheel-strike-spark"
            cx="350"
            cy="350"
            r={s.r}
            fill={i % 3 ? "#fff1c0" : "#ffb347"}
            style={{ "--dx": `${s.dx.toFixed(1)}px`, "--dy": `${s.dy.toFixed(1)}px`, animationDelay: `${260 + s.delay}ms` } as React.CSSProperties}
          />
        ))}
      </g>
    </g>
  );
};
